import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexPlotOptions,
  ApexFill,
  ApexStroke,
  ApexTooltip,
  ApexDataLabels,
  ApexLegend,
  NgApexchartsModule,
  ChartComponent,
  ApexXAxis,
  ApexYAxis,
  ApexGrid,
} from "ng-apexcharts";
import { MaterialModule } from "src/app/material.module";
import { DashboardChartEmptyStateComponent } from "../../shared/dashboard-chart-empty-state/dashboard-chart-empty-state.component";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  fill: ApexFill;
  legend: ApexLegend;
  colors: string[];
  grid: ApexGrid;
};

@Component({
  selector: "app-case-overview-chart",
  standalone: true,
  imports: [NgApexchartsModule, MaterialModule, TranslateModule, DashboardChartEmptyStateComponent],
  templateUrl: "./case-overview-chart.component.html",
  styleUrls: ["./case-overview-chart.component.scss"],
})
export class CaseOverviewChartComponent
  implements OnInit, OnDestroy, OnChanges {
  @ViewChild("chart") chart: ChartComponent;

  readonly translate = inject(TranslateService);
  public chartOptions: ChartOptions;

  @Input({ required: true }) filedCasesAmount: number = 0;
  @Input({ required: true }) filedCasesTotal: number = 0;

  @Input({ required: true }) cashReimbursementAmount: number = 0;
  @Input({ required: true }) cashReimbursementTotal: number = 0;
  @Input({ required: true }) inventoryReimbursementAmount: number = 0;
  @Input({ required: true }) inventoryReimbursementTotal: number = 0;

  @Output() readonly viewCases = new EventEmitter<void>();

  get hasData(): boolean {
    return (
      this.filedCasesAmount > 0 ||
      this.filedCasesTotal > 0 ||
      this.cashReimbursementAmount > 0 ||
      this.cashReimbursementTotal > 0 ||
      this.inventoryReimbursementAmount > 0 ||
      this.inventoryReimbursementTotal > 0
    );
  }

  private mutationObserver: MutationObserver;

  constructor() {
    this.chartOptions = {
      series: [
        {
          name: "Valores",
          data: [0, 0, 0],
        },
      ],
      chart: {
        type: "bar",
        height: 180,
        width: "100%",
        toolbar: { show: false },
        offsetX: 0,
        offsetY: 0,
        parentHeightOffset: 0,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 8,
          borderRadiusApplication: "end",
          barHeight: "62%",
          distributed: true,
        },
      },
      xaxis: {
        categories: ["", "", ""],
        labels: {
          formatter: (value: string) => {
            const num = parseFloat(value);
            return isNaN(num) ? value : `${num.toLocaleString()}`;
          },
          style: {
            fontSize: "10px",
            colors: [], // populated dynamically
          },
        },
        position: "top",
        axisBorder: { show: true },
        axisTicks: { show: true },
      },
      yaxis: {
        labels: {
          show: false,
          maxWidth: 0,
        },
        axisBorder: { show: true },
        axisTicks: { show: false },
      },
      fill: { type: "solid" },
      stroke: { show: false },
      tooltip: { enabled: false },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `$ ${val.toLocaleString()}`,
        style: {
          colors: [], // populated dynamically
        },
      },
      legend: { show: false },
      colors: ["#FFD54F", "#FFB300", "#BA68C8"],
      grid: {
        show: true,
        borderColor: "var(--mat-sys-outline-variant)",
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } },
        padding: {
          left: 0,
          right: 8,
          top: 0,
          bottom: -10,
        },
      },
    };
  }

  ngOnInit(): void {
    this.updateColorsFromTheme();

    this.mutationObserver = new MutationObserver(() => {
      this.updateColorsFromTheme();
      this.updateSeries();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.updateSeries();

    // Update series when language changes
    this.translate.onLangChange.subscribe(() => {
      this.updateSeries();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["filedCasesAmount"] ||
      changes["cashReimbursementAmount"] ||
      changes["inventoryReimbursementAmount"] ||
      changes["filedCasesTotal"] ||
      changes["cashReimbursementTotal"] ||
      changes["inventoryReimbursementTotal"]
    ) {
      this.updateSeries();
    }
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private isDarkMode(): boolean {
    return document.documentElement.classList.contains("dark-theme");
  }

  private updateSeries(): void {
    if (this.chartOptions) {
      const amounts = [
        this.filedCasesAmount,
        this.cashReimbursementAmount,
        this.inventoryReimbursementAmount,
      ];

      const totals = [
        this.filedCasesTotal,
        this.cashReimbursementTotal,
        this.inventoryReimbursementTotal,
      ];

      const isDark = this.isDarkMode();

      this.chartOptions.series = [
        {
          name: "",
          data: totals,
        },
      ];

      this.chartOptions.dataLabels.formatter = (_val: number, opts: any) => {
        const amount = amounts[opts.dataPointIndex] || 0;
        return `$ ${amount.toLocaleString()}`;
      };

      this.chartOptions.xaxis.labels!.style!.colors = isDark
        ? ["#FFFFFF", "#FFFFFF", "#FFFFFF"]
        : ["#000000", "#000000", "#000000"];

      this.chartOptions.dataLabels.style!.colors = isDark
        ? ["#FFFFFF", "#FFFFFF", "#FFFFFF"]
        : ["#000000", "#000000", "#000000"];

      this.chartOptions.tooltip = {
        enabled: true,
        theme: "light",
        style: {
          fontSize: "13px",
          fontFamily: "inherit",
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
          const amount = amounts[dataPointIndex] || 0;
          const total = series[seriesIndex][dataPointIndex] || 0;
          const color = this.chartOptions.colors[dataPointIndex] || "#FFD54F";
          const totalLabel = this.translate.instant("dashboard.tooltip.total");
          const montoLabel = this.translate.instant("dashboard.tooltip.amount");
          const caseTypes = [
            this.translate.instant("dashboard.filed_cases"),
            this.translate.instant("dashboard.cash_reimbursement"),
            this.translate.instant("dashboard.inventory_reimbursement"),
          ];
          const caseType = caseTypes[dataPointIndex] || "";
          return `
            <div style="background: ${color}; padding: 10px 12px; border-radius: 6px; color: white; font-weight: 500; line-height: 1.4;">
              <div style="margin-bottom: 6px; font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.3); padding-bottom: 4px; line-height: 1.3;">${caseType}</div>
              <div style="line-height: 1.4; margin-bottom: 2px;">${totalLabel}: ${total.toLocaleString()}</div>
              <div style="line-height: 1.4;">${montoLabel}: $ ${amount.toLocaleString()}</div>
            </div>
          `;
        },
      };

      if (this.chart?.updateOptions) {
        this.chart.updateOptions({
          series: this.chartOptions.series,
          dataLabels: this.chartOptions.dataLabels,
          tooltip: this.chartOptions.tooltip,
          xaxis: {
            labels: {
              style: {
                colors: this.chartOptions.xaxis.labels!.style!.colors,
              },
            },
          },
        });
      }
    }
  }

  private updateColorsFromTheme(): void {
    const root = getComputedStyle(document.documentElement);
    const primary = root.getPropertyValue("--mat-charts-color-one").trim();
    const secondary = root.getPropertyValue("--mat-charts-color-two").trim();
    const tertiary = root.getPropertyValue("--mat-charts-color-three").trim();

    this.chartOptions.colors = [primary, secondary, tertiary];

    const isDark = this.isDarkMode();

    this.chartOptions.xaxis.labels!.style!.colors = isDark
      ? ["#FFFFFF", "#FFFFFF", "#FFFFFF"]
      : ["#000000", "#000000", "#000000"];

    this.chartOptions.dataLabels!.style!.colors = isDark
      ? ["#FFFFFF", "#FFFFFF", "#FFFFFF"]
      : ["#000000", "#000000", "#000000"];

    if (this.chart?.updateOptions) {
      this.chart.updateOptions({
        colors: this.chartOptions.colors,
        xaxis: {
          labels: {
            style: {
              colors: this.chartOptions.xaxis.labels!.style!.colors,
            },
          },
        },
      });
    }
  }
}
